import { createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../shared/utils/api.js'

export const createFeedback = createAsyncThunk('feedback/create', async (feedbackData, { rejectWithValue }) => {
  try {
    const response = await api.post('/v1/feedback', feedbackData)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const fetchAllFeedback = createAsyncThunk('feedback/fetchAll', async (params, { rejectWithValue }) => {
  try {
    let allFeedback = []
    let currentPage = 1
    let totalPages = 1
    
    const timestamp = params?.forceRefresh ? `&t=${params.forceRefresh}` : ''

    do {
      const response = await api.get(`/v1/feedback/admin/all?page=${currentPage}&limit=20${timestamp}`)
      
      const responseData = response.data.data || response.data
      const { feedback, pagination } = responseData
      
      if (Array.isArray(feedback)) {
        allFeedback = [...allFeedback, ...feedback]
      } else {
        if (feedback) {
          if (typeof feedback === 'object') {
            allFeedback = [...allFeedback, feedback]
          }
        }
      }
      
      if (pagination) {
        totalPages = pagination.totalPages
        currentPage++
      } else {
        const total = responseData.total || allFeedback.length
        const limit = responseData.limit || 20
        totalPages = Math.ceil(total / limit)
        currentPage++
      }
    } while (currentPage <= totalPages)
    
    return Array.isArray(allFeedback) ? allFeedback : []
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const updateFeedback = createAsyncThunk(
  'feedback/update',
  async ({ feedbackId, updatedData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/v1/feedback/admin/${feedbackId}`, updatedData)
      return response.data.feedback || response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const fetchFeedbackAnalytics = createAsyncThunk('feedback/analytics', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/v1/feedback/admin/analytics')
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const updateFeedbackStatus = createAsyncThunk(
  'feedback/updateStatus',
  async ({ feedbackId, status }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/v1/feedback/admin/${feedbackId}/status`, { status })
      return response.data.feedback || response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const markFeedbackOpened = createAsyncThunk(
  'feedback/markOpened',
  async (feedbackId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/v1/feedback/${feedbackId}/opened`)
      return response.data.feedback || response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const fetchUserFeedback = createAsyncThunk(
  'feedback/fetchUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/feedback/my')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)


export const fetchFeedbackById = createAsyncThunk(
  'feedback/fetchById',
  async (feedbackId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/v1/feedback/${feedbackId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const updateUserFeedback = createAsyncThunk(
  'feedback/updateUser',
  async ({ feedbackId, updatedData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/v1/feedback/${feedbackId}`, updatedData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const deleteUserFeedback = createAsyncThunk(
  'feedback/deleteUser',
  async (feedbackId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/v1/feedback/${feedbackId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const fetchAgentFeedback = createAsyncThunk(
  'feedback/fetchAgent',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/feedback/agent/my')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)


export const fetchAgentFeedbackById = createAsyncThunk(
  'feedback/fetchAgentById',
  async (feedbackId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/v1/feedback/agent/${feedbackId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const respondToFeedbackAsAgent = createAsyncThunk(
  'feedback/agentRespond',
  async ({ feedbackId, responseData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/v1/feedback/agent/${feedbackId}/respond`, responseData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const fetchAgentFeedbackAnalytics = createAsyncThunk(
  'feedback/agentAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/feedback/agent/analytics')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

// New endpoints for forward, reply, and unread
export const forwardFeedback = createAsyncThunk(
  'feedback/forward',
  async ({ feedbackId, forwardData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/v1/feedback/${feedbackId}/forward`, forwardData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const replyToFeedback = createAsyncThunk(
  'feedback/reply',
  async ({ feedbackId, replyData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/v1/feedback/${feedbackId}/reply`, replyData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const markFeedbackUnread = createAsyncThunk(
  'feedback/markUnread',
  async (feedbackId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/v1/feedback/${feedbackId}/unread`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)
